import {
  useV2CreateBusinessUnitMutation,
  useV2UpdateBusinessUnitMutation,
  useV2DeleteBusinessUnitMutation,
} from "@generated";
import type { V2BusinessUnitResource } from "@generated";
import type { BusinessUnitFormValues } from "../types";

// Build the JSON:API `data` body for create/update. The `account` relationship
// required by V2BusinessUnitResource is inferred from auth server-side, so we
// omit it and cast (same pattern as the Clients/Vendors MSPAs).
export const buildBusinessUnitData = (
  values: BusinessUnitFormValues,
  id?: string
): V2BusinessUnitResource => {
  const country =
    typeof values.country === "string"
      ? values.country
      : values.country?.id ?? "";

  return {
    type: "business-units",
    ...(id ? { id } : {}),
    attributes: {
      name: values.name,
      "external-name": values.externalName || "",
      "street-address": values.streetAddress || "",
      street2: values.street2 || "",
      city: values.city || "",
      state: values.state || "",
      "postal-code": values.postalCode || "",
      country,
    },
  } as unknown as V2BusinessUnitResource;
};

export const useBusinessUnitMutations = () => {
  const [create, createState] = useV2CreateBusinessUnitMutation();
  const [update, updateState] = useV2UpdateBusinessUnitMutation();
  const [destroy, destroyState] = useV2DeleteBusinessUnitMutation();

  return {
    create: (values: BusinessUnitFormValues) =>
      create({ body: { data: buildBusinessUnitData(values) } }).unwrap(),

    update: (id: string, values: BusinessUnitFormValues) =>
      update({
        id: id,
        body: { data: buildBusinessUnitData(values, id) },
      }).unwrap(),

    // Archive (soft-delete: the API stamps deleted_at; restore re-activates).
    archive: (id: string) => destroy({ id: id }).unwrap(),

    // `active` is not in the generated V2BusinessUnitResource schema; the cast
    // lets restore re-activate a soft-deleted unit (verified at runtime, plan Task 13).
    restore: (id: string, name: string) =>
      update({
        id: id,
        body: {
          data: {
            type: "business-units",
            id: id,
            attributes: { name, active: true },
          } as unknown as V2BusinessUnitResource,
        },
      }).unwrap(),

    states: { createState, updateState, destroyState },
  };
};

export default useBusinessUnitMutations;
